import {
  useFetchWaitingList,
  useFetchWaitingListTickets,
} from '@seaters/data-access';
import { Divider, Grid, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

interface PurchaseSummaryProps {
  /** Defaults to the route's waiting list. */
  waitingListId?: string;
}

export function PurchaseSummary({
  waitingListId: waitingListIdProp,
}: PurchaseSummaryProps = {}) {
  const { waitingListId = '', wishListId = '' } = useParams();
  const wlId = waitingListIdProp ?? (waitingListId || wishListId);
  const { t } = useTranslation();
  const { xs } = useBreakpoint();
  const { data: waitingList } = useFetchWaitingList(wlId);

  const { data: price } = useFetchWaitingListTickets(
    wlId,
    waitingList?.price?.numberOfSeats || 0
  );

  const numberOfGuests = waitingList?.price?.numberOfSeats || 0;

  const formattedFacialPricePerTicket = price?.formattedFacialPrice ?? null;
  const formattedFacialPriceTotal = price?.formattedTotalFacialPrice ?? null;
  const formattedServiceFeeTotal = price?.formattedFee ?? null;
  const formattedTotalPrice = price?.formattedTotal ?? null;

  if (!waitingList) return null;

  if (waitingList?.freeWaitingList) {
    return (
      <div className="flex flex-col gap-6 bg-[#f0f0f0] rounded-3xl">
        {/* Event Banner */}
        {waitingList.eventImageUrl && !xs && (
          <div className="w-full h-36 rounded-2xl overflow-hidden">
            <img
              src={waitingList.eventImageUrl}
              alt={waitingList.translatedEventName || 'Event'}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Summary Content */}
        <div className="flex flex-col gap-3">
          <Title
            level={4}
            className={cx(
              '!mb-0  !font-semibold',
              xs ? '!text-base' : '!text-xl'
            )}
          >
            {t('order_summary')}
          </Title>
          <div className="flex justify-between gap-2">
            <Text className="!text-base !leading-6">
              {waitingList?.price?.numberOfSeats} {t('guests_text')}
            </Text>
            <Text className="!text-base !leading-6 text-right">
              {t('component_waitinglist_price-free')}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 rounded-3xl">
      {/* Summary Content */}
      <div className="flex flex-col gap-3">
        <Title level={4} className="!mb-0 !text-xl !font-semibold !leading-7">
          {t('order_summary')}
        </Title>

        <div className="flex items-center justify-between gap-2">
          <Text className="!text-base !leading-6 flex-1">
            {formattedFacialPricePerTicket} x{' '}
            {numberOfGuests} {t('guests_text')}
          </Text>
          <Text className="!text-base !leading-6 text-right whitespace-nowrap">
            {formattedFacialPriceTotal}
          </Text>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Text className="!text-base !leading-6 flex-1">
            {t('waitinglist_fees', { numberOfSeats: numberOfGuests })}
          </Text>
          <Text className="!text-base !leading-6 text-right whitespace-nowrap">
            {formattedServiceFeeTotal}
          </Text>
        </div>

        <Divider className="m-0!" />

        <div className="flex items-center justify-between gap-2">
          <Text strong className="!text-base !font-semibold !leading-6 flex-1">
            {t('total')}
          </Text>
          <Text
            strong
            className="!text-base !font-semibold !leading-6 text-right whitespace-nowrap"
          >
            {formattedTotalPrice}
          </Text>
        </div>
      </div>
    </div>
  );
}
